import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart } from 'react-minimal-pie-chart';

import { withJobContext } from 'src/utils/context/JobContext';
import { DeferNotifications } from 'src/components/JobForm/DeferNotifications/DeferNotifications';
import { MainStatusesRowPropTypes } from 'src/components/JobForm/propTypes';
import { PageTitle } from 'src/components/Page';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import {
  UNDER_REVIEW,
  READY_FOR_INTERNAL_SAQ,
  UNDER_INTERNAL_SAQ,
  READY_FOR_EXTERNAL_SAQ,
  UNDER_EXTERNAL_SAQ,
  LOADING_RESULTS,
  READY_FOR_REVIEW, FINALIZED,
  READY_FOR_CAP
} from 'src/utils/data/job';
import Loader from 'src/components/Loader';
import moment from 'moment';
import { useUpdateSaqSubmissionRequested } from 'src/hooks/job';
import s from 'src/components/JobForm/JobForm.styles';

/* TODO: I know that component is not perfectly optimized (too many props) but that's just a quick separation from JobForm component */
const MainStatusesRow = ({
  jobContext,
  allAuditorActionsNeededCount,
  allNoteCount,
  allReviewerActionsNeededCount,
  allExternalActionsNeededCount,
  button,
  canUserLoadResults,
  closedNoteCount,
  currentUserPermissions,
  daysSinceConducted,
  dueDate,
  finishButtonAvailable,
  finishLoadingResultButton,
  isExternalUser,
  hasSaqService,
  hasUnverifiedSaqService,
  isLoading,
  importPastResultsButton,
  isSubmitting,
  job,
  onClick,
  onSetStatus,
  reopenLoadingResultButton,
  reopenReviewBtn,
  reopenCapBtn,
  setIsSubmitting,
  setShowSpellCheckPopup,
  showButton,
  showReopenCapButton,
  spellCheckButtonAvailable,
  statusLabel,
  startLoadingResultButton,
  startCAPWithoutReviewButton,
  userIsAuthorizedExternalSaqUser,
  userAuthorization,
  isDatasheetLoading,
  isStandaloneCAPJob,
  setShowError,
  validationErrors
}) => {
  const { t } = useTranslation();

  const { user: { isInternalUser }} = jobContext;

  const jobStatusesWithSaqReview = [
    UNDER_REVIEW,
    READY_FOR_INTERNAL_SAQ,
    UNDER_INTERNAL_SAQ,
    READY_FOR_EXTERNAL_SAQ,
    UNDER_EXTERNAL_SAQ,
  ];

  const jobStatusesInSaq = [
    READY_FOR_INTERNAL_SAQ,
    UNDER_INTERNAL_SAQ,
    READY_FOR_EXTERNAL_SAQ,
    UNDER_EXTERNAL_SAQ,
  ];

  const hasLoadingResultsPermission = currentUserPermissions?.some(permission => permission.resourceName === 'LoadingResult' && permission.name === 'change');
 
  const isUnderInternalSaq = job.status === UNDER_INTERNAL_SAQ;
  const isWithSaq = jobStatusesInSaq.includes(job.status);
  const canDeferNotifications = jobStatusesWithSaqReview.includes(job.status) && isInternalUser;
  const canReopenSaq = (userAuthorization.isAuditor || userAuthorization.isAdmin) && (isUnderInternalSaq || (hasUnverifiedSaqService && job.status === FINALIZED));
  const canStartLoadingResults = job.status !== LOADING_RESULTS && job.displayLoadingResults && hasLoadingResultsPermission;
  // const canStartCAPWithoutReviewButton = !isNewCapProcess && job.resultsLoadedAt && (job.status === READY_FOR_REVIEW) && (userAuthorization.isCapManager || userAuthorization.isAdmin);
  const isLoadingResults = job.status === LOADING_RESULTS && canUserLoadResults;
  const canReopenLoadingResults = ((!hasSaqService && job.status === READY_FOR_REVIEW) || (isStandaloneCAPJob && job.status === READY_FOR_CAP)) && moment(job.auditEndAt).isSameOrBefore() && hasLoadingResultsPermission;
  const canFinishLoadingResults = isLoadingResults && hasLoadingResultsPermission;
  const isReportReviewChartIsVisible = jobStatusesWithSaqReview.includes(job.status);
  const onUpdateSaqSubmissionRequested = useUpdateSaqSubmissionRequested();
  const externalUserSubmitedSAQ = async () =>{
    const response = await onUpdateSaqSubmissionRequested({ jobId: job.id });
    if(validationErrors) setShowError(true);
  }

  const reopenExternalSaqButton = (
    (canReopenSaq && !isLoading && !isSubmitting ? (
      <span>
          <s.StatusButton
            disabled={false}
            onClick={async () => {
              const event = hasUnverifiedSaqService ? 'reopen_unverified_saq' : 'reopen_external_saq';
              setIsSubmitting(true);
              await onSetStatus({
                jobId: job.id,
                event,
                time: new Date(),
              });
              setIsSubmitting(false);
            }}
            orange
            sm
            solid
            type="button"
          >
            {t('SEND_BACK_TO_SITE')}
          </s.StatusButton>
        </span>
    ) : null)
  );

  return (
    <s.FlexRow>
      <PageTitle>{job.reportNumber}</PageTitle>
      {isReportReviewChartIsVisible && (
        <s.Status>
          <s.PieChartDiv>
            <PieChart
              data={[
                {
                  color: '#55b047',
                  title: 'closedNoteCount',
                  value: allNoteCount === 0 ? 1 : closedNoteCount,
                },
                {
                  color: '#e32332',
                  title: 'openNoteCount',
                  value: allNoteCount === 0 ? 0 : allNoteCount - closedNoteCount,
                },
              ]}
              lineWidth={15}
              radius={50}
              startAngle={-90}
              lengthAngle={360}
              animate={false}
              animationDuration={500}
              animationEasing="ease-out"
              label={false}
              labelPosition={50}
              paddingAngle={0}
              style={{
                height: '40px',
                width: '40px',
              }}
              viewBoxSize={[100, 100]}
              segmentsStyle={{
                transition: 'stroke .3s ease',
              }}
              segmentsShift={0}
              onClick={undefined}
              onMouseOver={undefined}
              onMouseOut={undefined}
            />
            <s.PieChartText>
              {closedNoteCount}/{allNoteCount}
            </s.PieChartText>
          </s.PieChartDiv>
          <s.StatusText>
            {t('REPORT_REVIEWER_DASHBOARD_NOTES_CLOSED')}
          </s.StatusText>
          {isWithSaq ? (
            <s.StatusText>
              <span style={{ color: '#929497', marginRight: '15px', marginLeft: '0', }}>{t('ACTION_NEEDED')}</span>{' '}
              {t('AUDITOR')}: {allAuditorActionsNeededCount} {t('EXTERNAL')}:{' '}
              {allExternalActionsNeededCount}
            </s.StatusText>
          ) : (
            <s.StatusText>
              <span style={{ color: '#929497', marginRight: '15px', marginLeft: '0', }}>{t('ACTION_NEEDED')}</span>{' '}
              {t('AUDITOR')}: {allAuditorActionsNeededCount} {t('REVIEWER')}:{' '}
              {allReviewerActionsNeededCount}
            </s.StatusText>
          )}
        </s.Status>
      )}
      {canDeferNotifications && (
        <DeferNotifications jobId={job.id} />
      )}
      <s.Status>
        {!job.clientFinalReportSentAt ? (
          <s.ColorField status={daysSinceConducted < 7 ? 'green' : 'red'}>
            <s.FieldLabel>{t('DUE_DATE')}:</s.FieldLabel>
            <s.FieldText>{toSimpleUtcDateString(dueDate)}</s.FieldText>
          </s.ColorField>
        ) : null}
      </s.Status>
      <s.Status>
        <s.StatusText>{statusLabel}</s.StatusText>
        {reopenCapBtn}
        {reopenReviewBtn}
        {reopenExternalSaqButton}
        {showReopenCapButton && (!isLoading && !isSubmitting ? (
          <s.StatusButton
            onClick={async ()=>{
              setIsSubmitting(true);
              await onSetStatus({
                jobId: job.id,
                event: 'reopen_cap',
                time: new Date(),
              });
              setIsSubmitting(false);
            }}
            orange
            sm
            solid
            type="button"
          >
            Reopen Corrective Action Plan
          </s.StatusButton>
        ) : null )}
        {button && showButton && !isLoading && !isSubmitting && (
          <s.StatusButton
            disabled={!spellCheckButtonAvailable || (isExternalUser && !userIsAuthorizedExternalSaqUser)}
            onClick={() => {
              setShowSpellCheckPopup(true);
            }}
            orange
            sm
            solid
            type="button"
          >
            {t('SPELL_CHECK')}
          </s.StatusButton>
        )}
        {button &&
        showButton &&
        (!isLoading && !isSubmitting ? (
          <s.StatusButton
            disabled={((!finishButtonAvailable) || isDatasheetLoading || (isExternalUser && !userIsAuthorizedExternalSaqUser))}
            hide={!finishButtonAvailable && (button.key === 'START_EXTERNAL_SAQ' || job.status === FINALIZED )}
            onClick={async () => {
              if(userIsAuthorizedExternalSaqUser && job.status === UNDER_EXTERNAL_SAQ){
                externalUserSubmitedSAQ();
              }
              setIsSubmitting(true);
              onClick();
            }}
            orange
            sm
            solid
            type="button"
          >
            {button}
          </s.StatusButton>
        ) : null )}
        {canStartLoadingResults && startLoadingResultButton()}
        {/* {canStartCAPWithoutReviewButton && startCAPWithoutReviewButton()} */}
        {isLoadingResults && importPastResultsButton()}
        {canFinishLoadingResults && finishLoadingResultButton()}
        {canReopenLoadingResults && reopenLoadingResultButton()}
        {((isLoading || isSubmitting) && (
          <Loader u-ml-15 withText={false} />
        ))}
      </s.Status>
    </s.FlexRow>
  );
};

MainStatusesRow.propTypes = MainStatusesRowPropTypes;

export default React.memo(withJobContext(MainStatusesRow));
